import React from 'react'
import { useAppDispatch } from '../../hooks/redux'

import { Actions, taskCommentsReducer, initialState } from './reducer'

import { getComment, getComments, putComment } from '../../lib/api'
import { AddErrorAlert } from '../../slices/alert'

const TaskCommentsContext = React.createContext(null)

const TaskCommentsProvider = ({ children }) => {
  const appDispatch = useAppDispatch()

  const [state, dispatch] = React.useReducer(taskCommentsReducer, initialState)
  const [loading, setLoading] = React.useState([])
  const [eventQueue, setEventQueue] = React.useState([])
  const [hasModalWithComments, setHasModalWithComments] = React.useState(false)
  const [viewingTaskCommentsID, setViewingTaskCommentsID] = React.useState(null)
  const [shouldCheckClose, setShouldCheckClose] = React.useState(false)

  const loadComments = React.useCallback(
    async taskID => {
      // Check that an entry doesn't exist
      const alreadyLoaded = taskID in state.comments

      // Check that an existing entry has the correct count
      const loadedAll =
        (state.counts[taskID] ?? -1) ===
        Object.keys(state.comments[taskID] ?? {}).length

      // Check that a load is not already occuring
      const willLoad = loading.includes(taskID)

      if ((alreadyLoaded && loadedAll) || willLoad) {
        return
      }

      // Start load; add to loading queue
      setLoading(old => {
        if (old.includes(taskID)) {
          return old
        }

        return [...old, taskID]
      })
    },
    [state.comments, state.counts, loading]
  )

  const updateCounts = React.useCallback(tasks => {
    dispatch({ type: Actions.SET_INITIAL_COUNTS, tasks })
  }, [])

  const deleteComment = React.useCallback(
    async (taskID, commentID, onCountChange) => {
      try {
        dispatch({
          type: Actions.DELETE_COMMENT,
          taskID,
          commentID,
          onCountChange
        })
      } catch (err) {
        appDispatch(AddErrorAlert({ err }))
      }
    },
    [appDispatch]
  )

  const updateComment = React.useCallback(
    async (taskID, commentID, content) => {
      try {
        await putComment(taskID, commentID, { content })
        dispatch({ type: Actions.UPDATE_COMMENT, taskID, commentID, content })
      } catch (err) {
        appDispatch(AddErrorAlert({ err }))
      }
    },
    [appDispatch]
  )

  // Clears loading queue
  React.useEffect(() => {
    if (!loading.length) {
      return
    }

    Promise.all(
      loading.map(async taskID => {
        let comments = {}
        try {
          comments = await getComments(taskID)
        } catch (err) {
          console.error('Error getting comments', err)
        }

        // Finish load
        dispatch({ type: Actions.LOADED, comments, taskID })
      })
    ).then(() => setLoading([]))
  }, [loading])

  // Task comment events from socket should come here
  const handleEvent = React.useCallback(msg => {
    setEventQueue(old => [...old, msg])
  }, [])

  const processCommentEvent = React.useCallback(async msg => {
    const { truncated } = msg

    let comment = msg.comment
    const taskID = comment.task_id

    try {
      if (truncated && comment.id !== 'TEMP') {
        comment = await getComment(taskID, comment.id)
      }

      dispatch({ type: Actions.ADD, taskID, comment })
    } catch (err) {
      console.error('Error handling comment event', err)
    }
  }, [])

  // Handles socket events from the queue
  const processEvent = React.useCallback(
    async msg => {
      const { action_type: type } = msg
      if (type === 'comment') {
        await processCommentEvent(msg)
      } else {
        console.warn({ UNHANDLED_TASK_COMMENT: msg })
      }
    },
    [processCommentEvent]
  )

  // Processes and clears event queue
  React.useEffect(() => {
    if (!eventQueue.length) {
      return
    }

    eventQueue.forEach(processEvent)
    setEventQueue([])
  }, [eventQueue, processEvent])

  return (
    <TaskCommentsContext.Provider
      value={{
        comments: state.comments,
        counts: state.counts,
        hasModalWithComments,
        loadComments,
        handleEvent,
        updateCounts,
        processCommentEvent,
        deleteComment,
        updateComment,
        setHasModalWithComments,
        viewingTaskCommentsID,
        setViewingTaskCommentsID,
        shouldCheckClose,
        setShouldCheckClose
      }}
    >
      {children}
    </TaskCommentsContext.Provider>
  )
}

export default TaskCommentsProvider

export const useTaskComments = () => React.useContext(TaskCommentsContext)
